import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import { 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Download, 
  Clock, 
  Briefcase, 
  User, 
  LayoutGrid, 
  Menu, 
  ArrowUpDown,
  X
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import '../../Submissions/styles/Submissions.css';
import { usePersistentFilters } from '../../hooks/usePersistentFilters';

const ProgressChevron = ({ 
  activeStep = 0, 
  onStepClick, 
  counts = [], 
  rejectedStepCounts = [],
  withdrawnStepCounts = [],
  hiredStepCounts = [],
  hiringSteps = []
}) => {
  const stepsCount = hiringSteps.length > 0 ? hiringSteps.length : 10;
  return (
    <div className="progress-chevron-wrapper">
      <div className={`progress-chevron-bar total-${stepsCount}-steps`}>
        {[...Array(stepsCount)].map((_, index) => {
          const activeCount = counts[index] || 0;
          const rejCount = rejectedStepCounts[index] || 0;
          const withCount = withdrawnStepCounts[index] || 0;
          const hCount = hiredStepCounts[index] || 0;
          const totalAtStep = activeCount + rejCount + withCount + hCount;
          
          const stageName = hiringSteps[index]?.stepName || `Phase ${index + 1}`;
          
          let stepClass = 'empty';
          let tooltipDesc = `${stageName}: 0 Candidates`;

          if (rejCount > 0 && activeCount === 0 && withCount === 0 && hCount === 0) {
            stepClass = 'is-rejected';
            tooltipDesc = `${stageName}: ${rejCount} Rejected Candidate${rejCount === 1 ? '' : 's'}`;
          } else if (withCount > 0 && activeCount === 0 && rejCount === 0 && hCount === 0) {
            stepClass = 'is-withdrawn';
            tooltipDesc = `${stageName}: ${withCount} Withdrawn Candidate${withCount === 1 ? '' : 's'}`;
          } else if (hCount > 0 && activeCount === 0 && rejCount === 0 && withCount === 0) {
            stepClass = 'is-hired';
            tooltipDesc = `${stageName}: ${hCount} Hired Candidate${hCount === 1 ? '' : 's'}`;
          } else if (activeCount > 0 && rejCount === 0 && withCount === 0 && hCount === 0) {
            stepClass = 'is-active';
            tooltipDesc = `${stageName}: ${activeCount} Active Candidate${activeCount === 1 ? '' : 's'}`;
          } else if (totalAtStep > 0) {
            stepClass = 'is-mixed';
            const parts = [];
            if (activeCount > 0) parts.push(`${activeCount} Active`);
            if (rejCount > 0) parts.push(`${rejCount} Rejected`);
            if (withCount > 0) parts.push(`${withCount} Withdrawn`);
            if (hCount > 0) parts.push(`${hCount} Hired`);
            tooltipDesc = `${stageName}: ${parts.join(', ')}`;
          }
          
          return (
            <div 
              key={index} 
              className={`chevron-step step-${index + 1} ${stepClass}`}
              onClick={(e) => {
                e.stopPropagation();
                onStepClick && onStepClick(index + 1);
              }}
              title={tooltipDesc}
            >
              {totalAtStep}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const toYMD = (d) => {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const truncateText = (text, maxLength = 20) => {
  if (!text || text === 'N/A') return text || 'N/A';
  const str = String(text).trim();
  if (str.length <= maxLength) return str;
  return `${str.substring(0, maxLength)}...`;
};

export const BenchSubmissionDashboard = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [pageSize, setPageSize] = useState(() => {
    const saved = sessionStorage.getItem('bench_submission_pageSize');
    return saved !== null ? parseInt(saved, 10) : 25;
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    const saved = sessionStorage.getItem('bench_submission_searchQuery');
    return saved !== null ? saved : '';
  });
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [selectedClient, setSelectedClient] = useState(() => {
    const saved = sessionStorage.getItem('bench_submission_selectedClient');
    return saved !== null ? saved : '';
  });
  const [selectedRecruiter, setSelectedRecruiter] = useState(() => {
    const saved = sessionStorage.getItem('bench_submission_selectedRecruiter');
    return saved !== null ? saved : '';
  });

  useEffect(() => {
    sessionStorage.setItem('bench_submission_pageSize', pageSize);
  }, [pageSize]);

  useEffect(() => {
    sessionStorage.setItem('bench_submission_searchQuery', searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    sessionStorage.setItem('bench_submission_selectedClient', selectedClient);
  }, [selectedClient]);

  useEffect(() => {
    sessionStorage.setItem('bench_submission_selectedRecruiter', selectedRecruiter);
  }, [selectedRecruiter]);
  
  const today = toYMD(new Date());
  const initialDateFilters = {
    quickSelect: 'Today',
    startDate: today,
    endDate: today,
    submissionStatusFilter: 'with_submissions'
  };
  const { 
    filters: dateFilters, 
    setFilters: setDateFilters, 
    handleClearFilter: handleClearDateFilters 
  } = usePersistentFilters('benchSubmissionDateFilters', initialDateFilters);

  const [appliedDateFilters, setAppliedDateFilters] = useState({
    startDate: dateFilters.startDate,
    endDate: dateFilters.endDate
  });
  
  // State for data
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = sessionStorage.getItem('bench_submission_currentPage');
    return saved !== null ? parseInt(saved, 10) : 1;
  });

  useEffect(() => {
    sessionStorage.setItem('bench_submission_currentPage', currentPage);
  }, [currentPage]);

  const [hiringSteps, setHiringSteps] = useState([]);
  const [searchType, setSearchType] = useState('jobs'); // 'jobs' or 'candidates'
  const [candidatesData, setCandidatesData] = useState([]);
  const [benchRecruitersList, setBenchRecruitersList] = useState([]);

  const submissionStatusFilter = (dateFilters.submissionStatusFilter && dateFilters.submissionStatusFilter !== 'all') 
    ? dateFilters.submissionStatusFilter 
    : 'with_submissions';
  const setSubmissionStatusFilter = (v) => setDateFilters(p => ({ 
    ...p, 
    submissionStatusFilter: typeof v === 'function' ? v(p.submissionStatusFilter || 'with_submissions') : v 
  }));

  // Fetch bench recruiters for dropdown
  useEffect(() => {
    const fetchRecruiters = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/bench-manage/recruiters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success && Array.isArray(res.data.recruiters)) {
          setBenchRecruitersList(res.data.recruiters);
        }
      } catch (err) {
        console.error('Error fetching bench recruiters:', err);
      }
    };
    fetchRecruiters();
  }, []);

  const fetchHiringSteps = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/recruitment/applications/hiring-steps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const progressive = (response.data || []).filter(s => {
        const code = (s.stageCode || '').toUpperCase();
        const name = (s.stepName || '').toLowerCase().trim();
        return !['REJECTED', 'WITHDRAWN', 'REJECTED_INTERNAL', 'WITHDRAWN_INTERNAL', 'HOLD', 'ON_HOLD', 'HOLD_INTERNAL'].includes(code) &&
               !name.includes('rejected') && !name.includes('withdrawn') && !name.includes('hold');
      });
      setHiringSteps(progressive);
    } catch (error) {
      console.error('Error fetching hiring steps:', error);
    }
  }, []);

  const fetchBenchSubmissions = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/bench-manage/submitted-roles`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const mappedSubmissions = (response.data || []).map(role => {
        let loc = role.location || 'N/A';
        if (loc !== 'N/A') {
          loc = loc.split(',')
            .map(p => p.trim())
            .filter(p => p && p !== '.')
            .join(', ');
        }

        return {
          id: role.id,
          jobId: `JOB-${String(role.id).padStart(5, '0')}`,
          externalJobRef: role.externalJobRef || role.jobId || 'N/A',
          clientName: role.clientName || 'N/A',
          jobTitle: role.jobTitle || 'N/A',
          employmentType: role.employmentType || 'Contract',
          experience: role.experience || 'N/A',
          location: loc,
          rate: `${role.currency || '$'} ${role.minRate || 0} - ${role.maxRate || 0}`,
          progress: role.progress === null ? 0 : role.progress,
          applicationCount: role.applicationCount !== undefined ? role.applicationCount : 0,
          stepCounts: role.stepCounts || '',
          rejectedStepCounts: role.rejectedStepCounts || '',
          withdrawnStepCounts: role.withdrawnStepCounts || '',
          hiredStepCounts: role.hiredStepCounts || '',
          rejectedCount: Number(role.rejectedCount) || 0,
          rejectedBreakdown: role.rejectedBreakdown || '',
          withdrawnCount: Number(role.withdrawnCount) || 0,
          hiredCount: Number(role.hiredCount) || 0,
          createdAt: role.createdAt,
          recruiters: role.recruiters || ['B'],
          description: role.jobDescription || '',
          recruiterName: role.recruiterName || 'N/A'
        };
      });

      setSubmissions(mappedSubmissions);
    } catch (error) {
      console.error('Error fetching bench submissions:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchAllBenchSubmissions = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/bench-manage/all-submissions`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const mappedCandidates = (response.data || []).map(cand => {
        let loc = cand.location || 'N/A';
        if (loc !== 'N/A') {
          loc = loc.split(',')
            .map(p => p.trim())
            .filter(p => p && p !== '.')
            .join(', ');
        }
        return {
          ...cand,
          id: cand.id,
          candidateName: cand.candidateName || 'N/A',
          jobCode: cand.jobCode ? `JOB-${String(cand.jobCode).padStart(5, '0')}` : 'N/A',
          jobTitle: cand.jobTitle || 'N/A',
          clientName: cand.clientName || 'N/A',
          email: cand.candidateEmail || 'N/A',
          phone: cand.candidatePhone || 'N/A',
          experience: cand.experience || 'N/A',
          expectedSalary: cand.expectedSalary || 'N/A',
          location: loc,
          status: cand.status || 'ACTIVE',
          progress: cand.currentStep || 0,
          submittedBy: cand.submittedBy || 'Bench Recruiter',
          createdAt: cand.appliedAt,
          jobLocation: cand.jobLocation || 'N/A'
        };
      });

      setCandidatesData(mappedCandidates);
    } catch (error) {
      console.error('Error fetching all bench candidate submissions:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchType === 'jobs') {
      fetchBenchSubmissions();
    } else {
      fetchAllBenchSubmissions();
    }
    fetchHiringSteps();
  }, [searchType, fetchBenchSubmissions, fetchAllBenchSubmissions, fetchHiringSteps]);

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Distinct clients list
  const clientsList = useMemo(() => {
    const clientSet = new Set();
    const startStr = appliedDateFilters.startDate || dateFilters.startDate;
    const endStr = appliedDateFilters.endDate || dateFilters.endDate;

    const isDateMatch = (itemDate) => {
      if (!startStr || !endStr || !itemDate) return true;
      const start = new Date(`${startStr}T00:00:00.000Z`);
      const end = new Date(`${endStr}T23:59:59.999Z`);
      const d = new Date(itemDate);
      return d >= start && d <= end;
    };

    submissions.forEach(s => {
      if (s.clientName && s.clientName !== 'N/A' && isDateMatch(s.createdAt)) {
        clientSet.add(s.clientName.trim());
      }
    });
    candidatesData.forEach(c => {
      if (c.clientName && c.clientName !== 'N/A' && isDateMatch(c.createdAt || c.appliedAt)) {
        clientSet.add(c.clientName.trim());
      }
    });
    return Array.from(clientSet).sort((a, b) => a.localeCompare(b));
  }, [submissions, candidatesData, appliedDateFilters, dateFilters]);

  // Recruiter options
  const recruiterOptions = useMemo(() => {
    const recSet = new Set();
    benchRecruitersList.forEach(r => {
      if (r.name) recSet.add(r.name);
    });
    candidatesData.forEach(c => {
      if (c.submittedBy && c.submittedBy !== 'N/A') recSet.add(c.submittedBy);
    });
    submissions.forEach(s => {
      if (s.recruiterName && s.recruiterName !== 'N/A') recSet.add(s.recruiterName);
    });
    return Array.from(recSet).sort();
  }, [benchRecruitersList, candidatesData, submissions]);

  // Base Filtered Jobs
  const baseFilteredJobs = useMemo(() => {
    let result = submissions;

    if (searchQuery.trim()) {
      const queryParts = searchQuery.toLowerCase().split(',').map(p => p.trim()).filter(Boolean);
      result = result.filter(s => (
        queryParts.every(part => (
          (s.clientName && s.clientName.toLowerCase().includes(part)) ||
          (s.jobTitle && s.jobTitle.toLowerCase().includes(part)) ||
          (s.jobId && String(s.jobId).toLowerCase().includes(part)) ||
          (s.externalJobRef && String(s.externalJobRef).toLowerCase().includes(part)) ||
          (s.employmentType && s.employmentType.toLowerCase().includes(part)) ||
          (s.experience && String(s.experience).toLowerCase().includes(part)) ||
          (s.location && s.location.toLowerCase().includes(part)) ||
          (s.recruiterName && s.recruiterName.toLowerCase().includes(part))
        ))
      ));
    }

    if (selectedClient.trim()) {
      result = result.filter(s => (s.clientName || '').toLowerCase().includes(selectedClient.toLowerCase().trim()));
    }

    if (selectedRecruiter.trim()) {
      result = result.filter(s => (s.recruiterName || '').toLowerCase().includes(selectedRecruiter.toLowerCase().trim()));
    }

    // Date Filtering
    if (appliedDateFilters.startDate && appliedDateFilters.endDate) {
      const start = new Date(`${appliedDateFilters.startDate}T00:00:00.000Z`);
      const end = new Date(`${appliedDateFilters.endDate}T23:59:59.999Z`);
      
      result = result.filter(s => {
        if (!s.createdAt) return true;
        const sDate = new Date(s.createdAt);
        return sDate >= start && sDate <= end;
      });
    }

    return result;
  }, [submissions, searchQuery, selectedClient, selectedRecruiter, appliedDateFilters]);

  // Role Counts
  const withSubmissionsCount = useMemo(() => baseFilteredJobs.filter(s => (s.applicationCount || 0) > 0 || (s.progress || 0) > 0).length, [baseFilteredJobs]);
  const withoutSubmissionsCount = useMemo(() => baseFilteredJobs.filter(s => (s.applicationCount || 0) === 0 && (s.progress || 0) === 0).length, [baseFilteredJobs]);

  // Filtered & Sorted Submissions / Candidates
  const filteredSubmissions = useMemo(() => {
    if (searchType === 'candidates') {
      let result = candidatesData;

      if (searchQuery.trim()) {
        const queryParts = searchQuery.toLowerCase().split(',').map(p => p.trim()).filter(Boolean);
        result = result.filter(s => (
          queryParts.every(part => (
            (s.clientName && s.clientName.toLowerCase().includes(part)) ||
            (s.jobTitle && s.jobTitle.toLowerCase().includes(part)) ||
            (s.jobCode && String(s.jobCode).toLowerCase().includes(part)) ||
            (s.candidateName && s.candidateName.toLowerCase().includes(part)) ||
            (s.email && s.email.toLowerCase().includes(part)) ||
            (s.phone && s.phone.toLowerCase().includes(part)) ||
            (s.submittedBy && s.submittedBy.toLowerCase().includes(part)) ||
            (s.jobLocation && s.jobLocation.toLowerCase().includes(part))
          ))
        ));
      }

      if (selectedClient.trim()) {
        result = result.filter(s => (s.clientName || '').toLowerCase().includes(selectedClient.toLowerCase().trim()));
      }

      if (selectedRecruiter.trim()) {
        result = result.filter(s => (s.submittedBy || '').toLowerCase().includes(selectedRecruiter.toLowerCase().trim()));
      }

      if (appliedDateFilters.startDate && appliedDateFilters.endDate) {
        const start = new Date(`${appliedDateFilters.startDate}T00:00:00.000Z`);
        const end = new Date(`${appliedDateFilters.endDate}T23:59:59.999Z`);
        result = result.filter(s => {
          if (!s.createdAt) return true;
          const sDate = new Date(s.createdAt);
          return sDate >= start && sDate <= end;
        });
      }

      if (sortConfig.key) {
        result.sort((a, b) => {
          if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
          if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return result;
    }

    // searchType === 'jobs'
    let result = [...baseFilteredJobs];

    if (submissionStatusFilter === 'without_submissions') {
      result = result.filter(s => (s.applicationCount || 0) === 0 && (s.progress || 0) === 0);
    } else {
      result = result.filter(s => (s.applicationCount || 0) > 0 || (s.progress || 0) > 0);
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [searchType, candidatesData, baseFilteredJobs, searchQuery, selectedClient, selectedRecruiter, appliedDateFilters, sortConfig, submissionStatusFilter]);

  // Pagination Logic
  const paginatedSubmissions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredSubmissions.slice(startIndex, startIndex + pageSize);
  }, [filteredSubmissions, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredSubmissions.length / pageSize) || 1;

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, appliedDateFilters, submissionStatusFilter, selectedClient, selectedRecruiter]);

  const formatTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60); // minutes
    if (diff < 60) return `${diff}m ago`;
    const hours = Math.floor(diff / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  const handleQuickSelectChange = (e) => {
    const val = e.target.value;
    const todayDate = new Date();
    let start = new Date(todayDate);
    let end = new Date(todayDate);

    if (val === 'Today') {
      start = new Date(todayDate);
      end = new Date(todayDate);
    } else if (val === 'Yesterday') {
      start = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate() - 1);
      end = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate() - 1);
    } else if (val === 'This Week') {
      const day = todayDate.getDay();
      const diff = todayDate.getDate() - day + (day === 0 ? -6 : 1); 
      start = new Date(todayDate.getFullYear(), todayDate.getMonth(), diff);
      end = new Date(todayDate);
    } else if (val === 'Last Week') {
      const day = todayDate.getDay();
      const diff = todayDate.getDate() - day + (day === 0 ? -6 : 1) - 7;
      start = new Date(todayDate.getFullYear(), todayDate.getMonth(), diff);
      end = new Date(todayDate.getFullYear(), todayDate.getMonth(), diff + 6);
    } else if (val === 'This Month') {
      start = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
      end = new Date(todayDate);
    } else if (val === 'Last Month') {
      start = new Date(todayDate.getFullYear(), todayDate.getMonth() - 1, 1);
      end = new Date(todayDate.getFullYear(), todayDate.getMonth(), 0);
    } else if (val === 'This Quarter') {
      const quarter = Math.floor(todayDate.getMonth() / 3);
      start = new Date(todayDate.getFullYear(), quarter * 3, 1);
      end = new Date(todayDate);
    } else if (val === 'Last Quarter') {
      const quarter = Math.floor(todayDate.getMonth() / 3) - 1;
      start = new Date(todayDate.getFullYear(), quarter * 3, 1);
      end = new Date(todayDate.getFullYear(), (quarter + 1) * 3, 0);
    } else if (val === 'All Time') {
      start = null;
      end = null;
    }

    const sDate = start ? toYMD(start) : '';
    const eDate = end ? toYMD(end) : '';

    setDateFilters({
      quickSelect: val,
      startDate: sDate,
      endDate: eDate
    });

    setAppliedDateFilters({
      startDate: sDate,
      endDate: eDate
    });
  };

  return (
    <div className="submissions-container">
      {/* Top Main Row: Title, Search, Controls (No Onsite/Offshore buttons) */}
      <div className="submissions-main-header">
        <div className="header-left-section">
          <h1 className="submissions-main-title">Submissions</h1>
          <div className="submissions-breadcrumbs">
            <span>Home</span>
            <span>&gt;</span>
            <span 
              onClick={() => setSearchType('jobs')}
              style={{ 
                cursor: 'pointer', 
                color: searchType === 'jobs' ? '#3b5bfd' : '#64748b', 
                fontWeight: searchType === 'jobs' ? '600' : 'normal',
                textDecoration: searchType === 'jobs' ? 'underline' : 'none'
              }}
            >
              Jobs
            </span>
            <span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span>
            <span 
              onClick={() => setSearchType('candidates')}
              style={{ 
                cursor: 'pointer', 
                color: searchType === 'candidates' ? '#3b5bfd' : '#64748b', 
                fontWeight: searchType === 'candidates' ? '600' : 'normal',
                textDecoration: searchType === 'candidates' ? 'underline' : 'none'
              }}
            >
              Candidates
            </span>
          </div>
        </div>

        <div className="header-middle-section">
          <div className="submissions-search-wrapper">
            <Search className="submissions-search-icon" size={16} />
            <input 
              type="text" 
              className="submissions-search-input" 
              placeholder="Filter by Client or Position..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="header-right-section" style={{ display: 'flex', alignItems: 'center' }}>
          <div className="submissions-controls">
            <button 
              className={`submissions-icon-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <LayoutGrid size={18} />
            </button>
            <button 
              className={`submissions-icon-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Date Filters Section */}
      <div style={{
        background: '#fff',
        borderRadius: '8px',
        padding: '12px 16px',
        margin: '0 0 16px 0',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Date filters:</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Quick Select</label>
              <select 
                value={dateFilters.quickSelect}
                onChange={handleQuickSelectChange}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  fontSize: '14px',
                  minWidth: '140px',
                  color: '#334155'
                }}
              >
                <option value="Today">Today</option>
                <option value="Yesterday">Yesterday</option>
                <option value="This Week">This Week</option>
                <option value="Last Week">Last Week</option>
                <option value="This Month">This Month</option>
                <option value="Last Month">Last Month</option>
                <option value="This Quarter">This Quarter</option>
                <option value="Last Quarter">Last Quarter</option>
                <option value="All Time">All Time</option>
                <option value="Custom">Custom</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Start date</label>
              <input 
                type="date"
                value={dateFilters.startDate}
                onChange={(e) => setDateFilters({ ...dateFilters, startDate: e.target.value, quickSelect: 'Custom' })}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  fontSize: '14px',
                  color: '#334155'
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>End date</label>
              <input 
                type="date"
                value={dateFilters.endDate}
                onChange={(e) => setDateFilters({ ...dateFilters, endDate: e.target.value, quickSelect: 'Custom' })}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  fontSize: '14px',
                  color: '#334155'
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Client</label>
              <select
                value={selectedClient}
                onChange={(e) => {
                  setSelectedClient(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  fontSize: '14px',
                  minWidth: '150px',
                  color: '#334155',
                  height: '38px',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Clients</option>
                {clientsList.map((cName, idx) => (
                  <option key={idx} value={cName}>{cName}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Bench Recruiter</label>
              <select
                value={selectedRecruiter}
                onChange={(e) => {
                  setSelectedRecruiter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  fontSize: '14px',
                  minWidth: '160px',
                  color: '#334155',
                  height: '38px',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Bench Recruiters</option>
                {recruiterOptions.map((name, idx) => (
                  <option key={idx} value={name}>{name}</option>
                ))}
              </select>
            </div>

            <button 
              style={{
                padding: '8px 24px',
                borderRadius: '6px',
                border: '1px solid #3b5bfd',
                background: '#eff6ff',
                color: '#3b5bfd',
                fontWeight: '500',
                fontSize: '14px',
                cursor: 'pointer',
                height: '38px'
              }}
              onClick={() => {
                setAppliedDateFilters({
                  startDate: dateFilters.startDate,
                  endDate: dateFilters.endDate
                });
              }}
            >
              Apply
            </button>
          </div>
        </div>

        <div>
          {(dateFilters.quickSelect !== 'Today' || 
            dateFilters.startDate !== today || 
            dateFilters.endDate !== today || 
            searchQuery !== '' ||
            selectedClient !== '' ||
            selectedRecruiter !== ''
          ) && (
            <button
              style={{
                padding: '8px 20px',
                borderRadius: '6px',
                border: '1px solid #e74c3c',
                background: '#fff5f5',
                color: '#e74c3c',
                fontWeight: '500',
                fontSize: '14px',
                cursor: 'pointer',
                height: '38px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onClick={() => {
                handleClearDateFilters();
                const todayVal = toYMD(new Date());
                setAppliedDateFilters({
                  startDate: todayVal,
                  endDate: todayVal
                });
                setSelectedClient('');
                setSelectedRecruiter('');
                sessionStorage.removeItem('bench_submission_selectedClient');
                sessionStorage.removeItem('bench_submission_selectedRecruiter');
                setSearchQuery('');
                setPageSize(25);
                setCurrentPage(1);
                sessionStorage.removeItem('bench_submission_pageSize');
                sessionStorage.removeItem('bench_submission_currentPage');
                sessionStorage.removeItem('bench_submission_searchQuery');
              }}
            >
              <X size={15} /> Clear All Filters
            </button>
          )}
        </div>
      </div>

      {/* Role Submission Status Filter (When in Jobs searchType) */}
      {searchType === 'jobs' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          margin: '0 0 16px 0',
          padding: '10px 16px',
          background: '#fff',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>
            Filter Roles:
          </span>
          <div style={{ display: 'flex', gap: '6px', background: '#f8fafc', padding: '3px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <button
              type="button"
              onClick={() => setSubmissionStatusFilter('with_submissions')}
              style={{
                padding: '5px 12px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: submissionStatusFilter === 'with_submissions' ? 700 : 500,
                background: submissionStatusFilter === 'with_submissions' ? '#3b5bfd' : 'transparent',
                color: submissionStatusFilter === 'with_submissions' ? '#fff' : '#64748b',
                transition: 'all 0.15s ease'
              }}
            >
              With Submissions ({withSubmissionsCount})
            </button>
            <button
              type="button"
              onClick={() => setSubmissionStatusFilter('without_submissions')}
              style={{
                padding: '5px 12px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: submissionStatusFilter === 'without_submissions' ? 700 : 500,
                background: submissionStatusFilter === 'without_submissions' ? '#ef4444' : 'transparent',
                color: submissionStatusFilter === 'without_submissions' ? '#fff' : '#64748b',
                transition: 'all 0.15s ease'
              }}
            >
              ⚠️ Without Submissions ({withoutSubmissionsCount})
            </button>
          </div>
        </div>
      )}

      {/* Conditional Rendering based on viewMode & searchType */}
      {viewMode === 'table' || searchType === 'candidates' ? (
        <div className="submissions-table-wrapper">
          <table className="submissions-table">
            <thead>
              {searchType === 'candidates' ? (
                <tr>
                  <th onClick={() => handleSort('candidateName')} className="sortable-th">
                    Candidate Name {sortConfig.key === 'candidateName' && <ArrowUpDown size={12} />}
                  </th>
                  <th onClick={() => handleSort('jobTitle')} className="sortable-th">
                    Job Title {sortConfig.key === 'jobTitle' && <ArrowUpDown size={12} />}
                  </th>
                  <th onClick={() => handleSort('clientName')} className="sortable-th">
                    Client {sortConfig.key === 'clientName' && <ArrowUpDown size={12} />}
                  </th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Exp</th>
                  <th>Location</th>
                  <th>Progress</th>
                  <th>Submitted By</th>
                  <th>Created</th>
                </tr>
              ) : (
                <tr>
                  <th onClick={() => handleSort('clientName')} className="sortable-th">
                    Client {sortConfig.key === 'clientName' && <ArrowUpDown size={12} />}
                  </th>
                  <th>Job ID</th>
                  <th>External Job Ref</th>
                  <th onClick={() => handleSort('jobTitle')} className="sortable-th">
                    Job Title {sortConfig.key === 'jobTitle' && <ArrowUpDown size={12} />}
                  </th>
                  <th>Type</th>
                  <th>Exp</th>
                  <th>Location</th>
                  <th>Technical Progress</th>
                  <th>Created</th>
                </tr>
              )}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={searchType === 'candidates' ? "10" : "9"}>
                    <div className="submissions-loader-container">
                      <div className="submissions-loader"></div>
                      <div className="submissions-loader-text">Loading bench submissions...</div>
                    </div>
                  </td>
                </tr>
              ) : paginatedSubmissions.length > 0 ? (
                paginatedSubmissions.map((row) => (
                  searchType === 'candidates' ? (
                    <tr 
                      key={row.id} 
                      onClick={() => navigate(`/submissions/${row.jobId}`, { state: { jobInfo: row } })}
                      style={{ cursor: 'pointer' }}
                      className="clickable-row"
                    >
                      <td className="client-name" style={{ fontWeight: '600' }}>{row.candidateName}</td>
                      <td>
                        <span style={{ fontSize: '13px', fontWeight: '500' }} title={row.jobTitle}>
                          {truncateText(row.jobTitle, 20)}
                        </span>
                      </td>
                      <td className="client-name" title={row.clientName}>
                        {truncateText(row.clientName, 20)}
                      </td>
                      <td style={{ fontSize: '12px' }}>{row.email}</td>
                      <td style={{ fontSize: '11px', color: '#64748b' }}>{row.phone}</td>
                      <td>{row.experience}</td>
                      <td className="na-text" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.location}>
                        {row.location}
                      </td>
                      <td title={hiringSteps[row.progress]?.stepName || `Step ${row.progress + 1}`}>
                        <span style={{ fontSize: '12px', fontWeight: '500', color: '#3b5bfd' }}>
                          {hiringSteps[row.progress]?.stepName || `Step ${row.progress + 1}`}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                          padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600
                        }}>
                          {row.submittedBy}
                        </span>
                      </td>
                      <td>{formatTime(row.createdAt)}</td>
                    </tr>
                  ) : (
                    <tr 
                      key={row.id} 
                      onClick={() => navigate(`/submissions/${row.id}`, { state: { jobInfo: row } })}
                      style={{ cursor: 'pointer' }}
                      className="clickable-row"
                    >
                      <td className="client-name" title={row.clientName}>
                        {truncateText(row.clientName, 20)}
                      </td>
                      <td style={{ fontSize: '11px', color: '#64748b' }}>{row.jobId}</td>
                      <td style={{ fontSize: '13px', fontWeight: '500', color: '#3b5bfd' }}>{row.externalJobRef}</td>
                      <td>
                        <Link to={`/submissions/${row.id}`} state={{ jobInfo: row }} className="job-title-link" title={row.jobTitle}>
                          {truncateText(row.jobTitle, 20)}
                        </Link>
                        {row.applicationCount === 0 && (
                          <span style={{ marginLeft: '8px', fontSize: '10px', background: '#fef2f2', color: '#dc2626', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                            0 Subs
                          </span>
                        )}
                      </td>
                      <td>{row.employmentType}</td>
                      <td>{row.experience}</td>
                      <td className="na-text" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.location}>
                        {row.location}
                      </td>
                      <td>
                        <ProgressChevron 
                          activeStep={row.progress} 
                          counts={row.stepCounts ? row.stepCounts.split(',').map(Number) : []}
                          rejectedStepCounts={row.rejectedStepCounts ? row.rejectedStepCounts.split(',').map(Number) : []}
                          withdrawnStepCounts={row.withdrawnStepCounts ? row.withdrawnStepCounts.split(',').map(Number) : []}
                          hiredStepCounts={row.hiredStepCounts ? row.hiredStepCounts.split(',').map(Number) : []}
                          hiringSteps={hiringSteps}
                        />
                      </td>
                      <td>{formatTime(row.createdAt)}</td>
                    </tr>
                  )
                ))
              ) : (
                <tr>
                  <td colSpan={searchType === 'candidates' ? "10" : "9"} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                    {submissionStatusFilter === 'without_submissions' 
                      ? 'No bench roles without submissions match your search.' 
                      : 'No bench submissions match your search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="submissions-grid-wrapper">
          {isLoading ? (
            <div className="submissions-loader-container">
              <div className="submissions-loader"></div>
              <div className="submissions-loader-text">Loading bench submissions...</div>
            </div>
          ) : paginatedSubmissions.length > 0 ? (
            paginatedSubmissions.map((row) => (
              <div key={row.id} className="submission-card">
                <div className="card-top-status">Bench Active</div>
                
                <div className="card-main-content">
                  {/* Left Section: Info */}
                  <div className="card-info-section">
                    <div className="building-icon-box">
                      <Briefcase size={24} className="building-icon" />
                    </div>
                    <div className="info-details">
                      <div className="client-id-row" title={row.clientName}>
                        <span title={row.clientName}>{truncateText(row.clientName, 20)}</span> | {row.jobId} | External Ref: <span style={{ color: '#3b5bfd', fontWeight: '500' }}>{row.externalJobRef}</span>
                      </div>
                      <h3 className="job-title-card" title={row.jobTitle}>
                        <Link to={`/submissions/${row.id}`} state={{ jobInfo: row }} style={{ textDecoration: 'none', color: 'inherit' }} title={row.jobTitle}>
                          {truncateText(row.jobTitle, 20)}
                        </Link>
                        <span className="type-badge">{row.employmentType}</span>
                        {row.applicationCount === 0 && (
                          <span style={{ fontSize: '10px', background: '#fef2f2', color: '#dc2626', padding: '2px 6px', borderRadius: '4px', fontWeight: 600, marginLeft: '6px' }}>
                            0 Subs
                          </span>
                        )}
                      </h3>
                      <div className="meta-row">
                        <Clock size={12} /> <span>{row.experience}</span>
                      </div>
                      <div className="meta-row">
                        <User size={12} /> <span>{row.location}</span>
                      </div>
                    </div>
                  </div>

                  {/* Middle Sections: Budgets */}
                  <div className="card-budget-section">
                    <div className="budget-item">
                      <div className="budget-icon-group">
                        <Download size={14} /> 
                        <span className="budget-label">Rate</span>
                      </div>
                      <div className="budget-value">{row.rate}</div>
                    </div>
                  </div>

                  {/* Progress Section */}
                  <div className="card-progress-section">
                    <div className="pipeline-label">Hiring Pipeline</div>
                    <ProgressChevron 
                      activeStep={row.progress} 
                      counts={row.stepCounts ? row.stepCounts.split(',').map(Number) : []}
                      rejectedStepCounts={row.rejectedStepCounts ? row.rejectedStepCounts.split(',').map(Number) : []}
                      withdrawnStepCounts={row.withdrawnStepCounts ? row.withdrawnStepCounts.split(',').map(Number) : []}
                      hiredStepCounts={row.hiredStepCounts ? row.hiredStepCounts.split(',').map(Number) : []}
                      hiringSteps={hiringSteps}
                    />
                  </div>

                  {/* Right Section: Time & Action */}
                  <div className="card-action-section">
                    <div className="time-ago">{formatTime(row.createdAt)}</div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              No bench submissions found.
            </div>
          )}
        </div>
      )}

      {/* Pagination Footer */}
      <div style={{
        padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: '#64748b', background: '#fff',
        borderRadius: '0 0 8px 8px', marginTop: '16px', flexWrap: 'wrap', gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>
            Showing <strong style={{ color: '#1e293b' }}>{filteredSubmissions.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}–{Math.min(currentPage * pageSize, filteredSubmissions.length)}</strong> of <strong style={{ color: '#1e293b' }}>{filteredSubmissions.length}</strong>
          </span>
          <select
            value={pageSize}
            onChange={(e) => {
              const val = Number(e.target.value);
              setPageSize(val);
              setCurrentPage(1);
              sessionStorage.setItem('bench_submission_pageSize', val);
              sessionStorage.setItem('bench_submission_currentPage', 1);
            }}
            style={{
              padding: '3px 8px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              fontSize: '12px',
              color: '#334155',
              background: '#fff',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            disabled={currentPage <= 1}
            onClick={() => {
              const prev = Math.max(currentPage - 1, 1);
              setCurrentPage(prev);
              sessionStorage.setItem('bench_submission_currentPage', prev);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '5px 12px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: currentPage <= 1 ? '#cbd5e1' : '#64748b',
              cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: 500
            }}
          >
            <ChevronLeft size={14} /> Prev
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
            .map((p, idx, arr) => {
              const prev = arr[idx - 1];
              return (
                <React.Fragment key={p}>
                  {prev && p - prev > 1 && (
                    <span style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>
                  )}
                  <button
                    onClick={() => {
                      setCurrentPage(p);
                      sessionStorage.setItem('bench_submission_currentPage', p);
                    }}
                    style={{
                      minWidth: '28px',
                      height: '28px',
                      padding: '0 6px',
                      borderRadius: '6px',
                      border: p === currentPage ? 'none' : '1px solid #e2e8f0',
                      background: p === currentPage ? '#3b5bfd' : '#fff',
                      color: p === currentPage ? '#fff' : '#64748b',
                      fontWeight: p === currentPage ? 700 : 500,
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {p}
                  </button>
                </React.Fragment>
              );
            })}

          <button
            disabled={currentPage >= totalPages}
            onClick={() => {
              const next = Math.min(currentPage + 1, totalPages);
              setCurrentPage(next);
              sessionStorage.setItem('bench_submission_currentPage', next);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '5px 12px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: currentPage >= totalPages ? '#cbd5e1' : '#64748b',
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: 500
            }}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BenchSubmissionDashboard;
